import React, { ReactNode, useCallback, useEffect, useState } from "react";
import { map, take } from "lodash";
import { Spring } from "react-spring/renderprops";
import { List } from "react-virtualized";
import styled from "styled-components";
import Pagination from "@charty/pagination";
import { Spacing, content } from "@glossy";
import { Locale } from "@linkfluence/models/locale";
import { usePages, useSize } from "@lkf/hooks";
import { getFullTotal, getMaxCount, useSortedGauges } from "./data";
import {
  Gauge,
  LINE_HEIGHT,
  MainUnit,
  Order,
  SecondaryUnit,
  Sort,
} from "./model";
import Row from "./row";

interface IProps {
  locale: Locale;
  gauges: Gauge[];
  onClickGauge?: (
    uid: Gauge["id"],
    status?: "include" | "exclude",
    label?: string
  ) => void;
  tooltip?: (gauge: Gauge) => ReactNode;
  limit?: number;
  className?: string;
  showAvatar?: boolean;
  showIndex?: boolean;
  showTrackWhenSuperNarrow?: boolean;
  customColor?: string;
  total?: number;
  mainUnit?: MainUnit;
  sort?: Sort;
  order?: Order;
  secondaryValue?: SecondaryUnit;
  displayNullValues?: boolean;
  evolutionTooltip?: React.ReactNode;
  isNarrow?: boolean;
  aside?: React.ReactNode;
  disableAnimation?: boolean;
  isFullscreen?: boolean;
  isHideable?: boolean;
  isPNG?: boolean;
}

const GaugeContainer = styled.div<{
  height: string;
  hasBorderBottom?: boolean;
}>`
  max-height: ${(props) => props.height};
  overflow: hidden !important;
  border-top: 1px solid ${(props) => content(props, "tertiary")};
  border-bottom: ${(props) =>
    props.hasBorderBottom && `1px solid ${content(props, "tertiary")}`};
  flex-grow: 1;
`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  column-gap: ${Spacing.Large};
`;

const StyledPagination = styled(Pagination)`
  margin-top: 16px;
`;

const HorizontalBarChart = ({
  gauges,
  limit = 5,
  locale,
  className,
  mainUnit,
  showAvatar,
  showIndex,
  showTrackWhenSuperNarrow,
  sort,
  order,
  customColor,
  secondaryValue,
  tooltip,
  displayNullValues,
  onClickGauge,
  total,
  evolutionTooltip,
  isNarrow,
  aside,
  disableAnimation = false,
  isFullscreen,
  isHideable,
  isPNG,
}: IProps) => {
  const [useDynamicList, setUseDynamicList] = useState(false);
  const ref = React.useRef();
  const { width } = useSize(ref);
  const maxCount = getMaxCount(gauges);

  const { sortedGauges, paginationTotal } = useSortedGauges(
    gauges,
    limit,
    sort,
    order,
    displayNullValues,
    isFullscreen
  );

  const showPagination = !isFullscreen && !isPNG && paginationTotal > limit;
  const { page, onPageReset, onPageUp, onPageDown } = usePages(
    paginationTotal,
    limit
  );
  const fullTotal = getFullTotal(total, sortedGauges);
  useEffect(() => {
    if (!isHideable) {
      onPageReset();
      setUseDynamicList(false);
    }
  }, [onPageReset, gauges, isHideable]);

  const _onClickGauge = useCallback(
    (gauge: Gauge) => {
      if (!gauge.isPhantom && !!onClickGauge) {
        onClickGauge(
          gauge.id,
          gauge.isHidden ? "exclude" : "include",
          gauge.label
        );
      }
    },
    [onClickGauge]
  );

  function rowRenderer({
    key,
    index,
    style,
    isScrolling,
  }: {
    key: string;
    index: number;
    style: any;
    isScrolling: boolean;
  }) {
    const gauge = sortedGauges[index];
    return (
      <Row
        gauge={gauge}
        key={key}
        index={index + 1}
        tooltip={tooltip}
        locale={locale}
        mainUnit={mainUnit}
        secondaryValue={secondaryValue}
        width={width}
        maxCount={maxCount}
        isHideable={isHideable}
        total={fullTotal}
        evolutionTooltip={evolutionTooltip}
        isNarrow={isNarrow}
        showAvatar={showAvatar}
        showIndex={showIndex}
        showTrackWhenSuperNarrow={showTrackWhenSuperNarrow}
        color={customColor}
        onClickGauge={onClickGauge && _onClickGauge}
        isTooltipDisabled={isScrolling || gauge.isPhantom}
        style={style}
      />
    );
  }

  return (
    <div className={className} ref={ref}>
      <Wrapper>
        <GaugeContainer
          height={isFullscreen ? "100%" : `${LINE_HEIGHT * limit}px`}
          hasBorderBottom={paginationTotal >= limit}
        >
          {useDynamicList ? (
            <Spring
              from={{ top: 0 }}
              to={{ top: LINE_HEIGHT * (page.from - 1) }}
            >
              {(props) => (
                <List
                  width={1}
                  height={LINE_HEIGHT * limit}
                  rowCount={sortedGauges.length}
                  rowHeight={LINE_HEIGHT}
                  rowRenderer={rowRenderer}
                  containerStyle={{
                    width: "100%",
                    maxWidth: "100%",
                  }}
                  style={{
                    width: "100%",
                    overflow: "hidden",
                  }}
                  scrollTop={props.top}
                />
              )}
            </Spring>
          ) : (
            <>
              {map(
                take(
                  sortedGauges,
                  isFullscreen ? sortedGauges.length : page.to
                ),
                (gauge, index) => (
                  <Row
                    gauge={gauge}
                    key={gauge.id}
                    index={index + 1}
                    tooltip={tooltip}
                    locale={locale}
                    mainUnit={mainUnit}
                    secondaryValue={secondaryValue}
                    width={width}
                    maxCount={maxCount}
                    total={fullTotal}
                    evolutionTooltip={evolutionTooltip}
                    isNarrow={isNarrow}
                    showAvatar={showAvatar}
                    showIndex={showIndex}
                    showTrackWhenSuperNarrow={showTrackWhenSuperNarrow}
                    color={customColor}
                    onClickGauge={onClickGauge && _onClickGauge}
                    isTooltipDisabled={gauge.isPhantom}
                    animate={isFullscreen ? false : !disableAnimation}
                    isHideable={isHideable}
                  />
                )
              )}
            </>
          )}
        </GaugeContainer>
        {aside}
      </Wrapper>
      {showPagination && (
        <div onMouseEnter={() => setUseDynamicList(true)}>
          <StyledPagination
            length={paginationTotal}
            page={page}
            locale={locale}
            onPageUp={onPageUp}
            onPageDown={onPageDown}
          />
        </div>
      )}
    </div>
  );
};

export default HorizontalBarChart;
